import { useEffect, useState, useCallback } from "react";
import { Bell, Newspaper, Activity } from "lucide-react";
import { getBot, getNews } from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s} sn önce`;
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
  return `${Math.floor(s / 86400)} gün önce`;
};

const SENT = { positive: "var(--pos)", negative: "var(--neg)", neutral: "var(--text-dim)" };

export const Notifications = () => {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  const load = useCallback(async () => {
    try {
      const [b, n] = await Promise.all([getBot(), getNews()]);
      setEvents(b?.events || []);
      setNews(n || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="notifications-view">
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5 text-[var(--pos)]" />
        <h2 className="font-head font-bold text-xl">Bildirimler</h2>
      </div>

      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
          <Activity className="h-4 w-4 text-[var(--accent)]" />
          <h3 className="font-head font-bold text-sm">Bot Etkinlikleri</h3>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {events.length === 0 ? (
            <p className="px-5 py-6 text-xs text-[var(--text-faint)]">Henüz bot etkinliği yok.</p>
          ) : events.map((e, i) => (
            <div key={i} className="px-5 py-3 flex gap-3" data-testid="notif-event">
              <span className="text-sm">{{ start: "🚀", open: "📈", close: "📉", stoploss: "🛑",
                takeprofit: "💰", trailing: "🔒", stop: "⏹️", expire: "⏱️", info: "🔍",
                plan: "🎯", dca_buy: "🟢" }[e.type] || "•"}</span>
              <div className="min-w-0">
                <p className="text-xs text-[var(--text-muted)] leading-relaxed">{e.message}</p>
                <span className="text-[10px] text-[var(--text-faint)] font-mono">{timeAgo(e.time)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-[var(--accent)]" />
          <h3 className="font-head font-bold text-sm">Piyasa Haberleri</h3>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {news.slice(0, 12).map((n, i) => (
            <div key={i} className="px-5 py-3" data-testid="notif-news">
              <div className="flex items-start gap-2">
                <span className="mt-1 h-2 w-2 rounded-full flex-shrink-0"
                  style={{ background: SENT[n.sentiment] || SENT.neutral }} />
                <div>
                  <p className="text-xs text-[var(--text-muted)] leading-relaxed">{n.title_tr || n.title}</p>
                  {n.summary_tr && <p className="text-[11px] text-[var(--text-faint)] mt-1">{n.summary_tr}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
